import React, { useEffect, useState } from 'react';
import '../solutions/Solutions.css'; // you can style it here
import { getDocs, collection } from 'firebase/firestore';
import { db } from '../../firebase'; // adjust based on your structure

// AI
// Data Center
// Central Office & Headend
// Outside plant
// FTTx & MDU
// Rural Broadband
// Wireless
// Enterprise
// Medical Imaging
// Neuroscience
// Optoelectronics
// Specialty Optics
// Optical Equipment

const slides = [
  {
    video: "/videos/solutionCentralOffice.mp4",
    poster: "/videos/solutionCentralOffice.jpg",
    heading: "The Importance of Fiber Density in AI Data Centers",
    subheading: "Sep 13, 2024",
    buttonLink: "/support"
  }
];

function BlogAIDataCenters() {

const [products, setProducts] = useState([]);
const [filteredProducts, setFilteredProducts] = useState([]);
const [activeFilter, setActiveFilter] = useState('All');

const [open, setOpen] = useState(new Set());

  const toggle = (key) => {
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };


const productFamilies = [
  'Fiber Cable Assemblies',
  'Fiber Panels',
  'Frames',
];

  const [industryFilter, setIndustryFilter] = useState('Central Office & Headends')

const topByFamily = productFamilies.map(famName => {
  const matchingProducts = filteredProducts.filter(
    p => (p.productFamily || '').trim() === famName
  );
  if (matchingProducts.length === 0) return null;

  const topProduct = matchingProducts.reduce((best, current) =>
    Number(current.rank ?? Infinity) < Number(best.rank ?? Infinity) ? current : best
  );

  return { family: famName, topProduct };
}).filter(Boolean); // remove nulls if a family has no products



useEffect(() => {
  const loadProducts = async () => {
    const querySnapshot = await getDocs(collection(db, 'products'));
    const allProducts = querySnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(p => (p["Include Y or N"] || '').trim().toUpperCase() !== 'N');

    setProducts(allProducts);
    setFilteredProducts(allProducts); // Default to all
  };

  loadProducts();
}, []);

useEffect(() => {
  if (activeFilter === 'All') {
    setFilteredProducts(products);
  } else {
    setFilteredProducts(products.filter(p => p.productFamily === activeFilter));
  }
}, [activeFilter, products]);

  return (
    <div className="contact-page">
      <div className="curved-header-sol2" style={{paddingBottom: 60}}>
        <div className="datacenter-left" style={{ paddingTop: 100}}>
            <h1>{slides[0].heading}</h1>
            <p style={{ marginBottom: 40, fontSize: 20}}>{slides[0].subheading}</p>
            </div>
            <div className="datacenter-right" style={{ paddingTop: 60}}>
              <img src="/images/services/image4.jpg" className="datacenter-img" />
            </div>
            </div>


      <div className="blog-section-1" style={{ marginTop: 30}}>

          <p className="solution-section-1-description">Fiber optics serve as the backbone of modern networks, connecting critical equipment and software. In AI data centers, fiber-dense infrastructure is essential for supporting the high bandwidth, low-latency, and scalable data transmission these environments demand. Even though fiber optics account for a small fraction of a data center’s total costs, understanding their key attributes is crucial for ensuring smooth performance in AI-driven applications.<br/><br/>

<b>1. High Bandwidth and Data Transfer</b><br/>

AI workloads, such as training deep learning models, involve processing massive amounts of data. Studies show that AI-focused data centers may need 2 to 4 times more fiber cabling than traditional hyperscale centers. Fiber optic cables, especially in dense configurations, provide significantly higher bandwidth than copper cables. This high bandwidth allows for faster transfers of large datasets, which are critical for AI tasks.<br/><br/>
Unlike traditional data centers, where traffic primarily moves north-south (client to server), AI data centers experience heavy east-west traffic (server to server). Since AI workloads rely on parallel processing, servers must communicate frequently with each other, requiring a more complex, fiber-dense network.<br/><br/>

<b>2. Low Latency</b><br/>
AI applications, particularly those involving real-time inference and decision-making, need extremely low latency to ensure fast data transmission. Fiber optics provide the low-latency connections needed for real-time processing, which is crucial for AI responsiveness.<br/><br/>
While fiber optic panels and connectors can add minor latency (measured in picoseconds or nanoseconds), reducing unnecessary connection points and using high-quality connectors can minimize these delays. This is particularly important for low-latency applications like AI.<br/><br/>

<b>3. Scalability</b><br/>
AI data centers need to scale quickly to handle increasing data and computational demands. A fiber-dense network allows for easy scalability, letting new servers and storage be added without major reconfigurations. As AI models and datasets grow, this scalable infrastructure is essential.<br/><br/>
However, expanding the network requires more ports to support parallel processing, making early strategic planning and investment key to managing costs and performance effectively.<br/><br/>

<b>4. Reliability and Signal Integrity</b><br/>
Reliability is critical in AI data centers. Fiber optics are less prone to electromagnetic interference (EMI) and signal degradation, ensuring stable and high-quality connections that AI operations can depend on.<br/><br/>

<b>5. Energy Efficiency</b><br/>
Compared to copper cables, fiber optics are more energy-efficient, helping reduce a data center’s overall energy consumption. This efficiency is especially important in large-scale AI operations. Proper cable management also boosts HVAC efficiency by ensuring adequate airflow around equipment, further reducing energy use.<br/><br/>

<b>6. Support for Advanced Networking Technologies</b><br/>
Fiber-dense infrastructure supports advanced networking technologies like Dense Wavelength Division Multiplexing (DWDM) and Software-Defined Networking (SDN), both of which are crucial for AI data centers. For instance, AI environments commonly require 400G and 800G connectivity, demanding more fibers and advanced solutions like multifiber push-on (MPO/MTP) connectors to handle the increased data rates.<br/><br/>
Additionally, the rise of fiber-dense infrastructure is driving the adoption of small form factor (VSFF) connectors and adapters, which can triple fiber density within the same space as traditional connectors.<br/><br/>

<b>7. Interconnects for Distributed AI Workloads</b><br/>
AI data centers often operate in distributed environments, where multiple data centers or cloud regions must be interconnected. Fiber optic infrastructure is ideal for these high-speed connections, enabling fast data transfers and synchronization between locations.<br/><br/>
In dynamic data centers, especially those supporting AI workloads, having flexible connectivity solutions is vital for easy changes and expansions:<br/><br/>

<ul style={{margin: 0}}>
    <li>Modular patch panels allow for quick re-routing and expansion without disrupting operations.</li>
    <li>Pre-terminated cables simplify installation and reduce downtime by offering plug-and-play connectivity.</li>
    <li>Multifiber connectors support high-density connections, enabling easy network reconfiguration.</li>
    <li>Automated infrastructure management (AIM) systems track connections in real time, making changes efficiently and minimizing errors, creating a future-proof network.</li>
  </ul><br/><br/>

For more information on data center connectivity for AI applications, visit below:<br/><br />
<a className="cta-button" style={{marginTop: 20}} href='solutions/datacenter'>
Learn More</a>
</p>
      </div>


        <div className="curved-footer-solutions">
        <div className="curved-top-overlay-solutions" style={{ backgroundColor: 'white'}}></div>
        
        <h2>Would you like to hear more?</h2>
        <p>Our experts would love to hear about your systems and how we can help</p>
        <a className="connect-button" href="/support">LET’S CONNECT</a>
        </div>
    </div>
  );
}

export default BlogAIDataCenters;
